/**
 * A read-through cache: the value for a key, worked out when it is first
 * asked for.
 */
export interface Cache<TKey, TValue> {
    get(key: TKey): TValue;
}

/**
 * A Cache that works each value out once and keeps it for as long as the
 * cache itself is kept. Nothing invalidates an entry, so a cache must not
 * outlive a change to anything its values were worked out from: make one for
 * a piece of work and drop it when the work is done. Subclasses say how a
 * value is worked out.
 */
export abstract class MemoCache<TKey, TValue> implements Cache<TKey, TValue> {
    // Each value is held in an entry, so an undefined value is still kept.
    private readonly entries = new Map<TKey, {value: TValue}>();

    /**
     * @param {TKey} key What the value is for.
     * @return {TValue} The value, worked out on first ask.
     */
    get(key: TKey): TValue {
        let entry = this.entries.get(key);
        if (entry === undefined) {
            entry = {value: this.create(key)};
            this.entries.set(key, entry);
        }

        return entry.value;
    }

    /**
     * Works out the value for a key the cache hasn't seen.
     * @param {TKey} key What the value is for.
     * @return {TValue} The value.
     */
    protected abstract create(key: TKey): TValue;
}
